import { DateTime } from 'luxon';
import { useApi } from 'lib/contexts/ApplicationState';
import { User } from 'lib/types/users';
import {
  useState, useEffect, Dispatch, SetStateAction, useRef,
} from 'react';
import { CreatorList } from '../LoggedInModules/CreatorList';

const CREATOR_LIST_SIZE = 6;

interface WeeklyCreatorResponse {
  creators: User[];
  endDate: string;
  startDate: string;
}

function datesToPostedAts(startDate: string, endDate: string) {
  // need to set year to current year, otherwise it will default to 2001
  const currentYear = DateTime.utc().year;
  // parse the dates as DateTime objects, setting the year to the current year
  const startDateWithCurrentYear = DateTime.fromFormat(startDate, 'M/d', { zone: 'utc' })
    .set({ year: currentYear })
    .toISODate();
  const endDateWithCurrentYear = DateTime.fromFormat(endDate, 'M/d', { zone: 'utc' })
    .set({ year: currentYear })
    .toISODate();
  return {
    minPostedAt: startDateWithCurrentYear,
    maxPostedAt: endDateWithCurrentYear,
  };
}

export const CreatorHighlights = () => {
  const API = useApi();
  const hasFetched = useRef(false);

  const [creators, setCreators] = useState<WeeklyCreatorResponse| undefined>();
  const [photographers, setPhotographers] = useState<WeeklyCreatorResponse | undefined>();
  const [videographers, setVideographers] = useState<WeeklyCreatorResponse | undefined>();
  const [artists, setArtists] = useState<WeeklyCreatorResponse | undefined>();

  useEffect(() => {
    // As soon as a request for creators gets its payload, immediately set the state.
    // This allows some creator rows to be rendered as fast as they can be fetched.
    const fetchAndSetCreators = async (
      userPromise: Promise<User[]>,
      setFunc: Dispatch<SetStateAction<User[] | undefined>>,
    ) => {
      const users = await API.ignoreErrors(userPromise, []);
      setFunc(users);
    };

    const getCreators = async () => {
      fetchAndSetCreators(API.topWeeklyCreators([], 'currentEngagements', CREATOR_LIST_SIZE), setCreators as any);
      fetchAndSetCreators(API.topWeeklyCreators([24], 'currentEngagements', CREATOR_LIST_SIZE), setPhotographers as any);
      fetchAndSetCreators(API.topWeeklyCreators([25], 'currentEngagements', CREATOR_LIST_SIZE), setVideographers as any);
      fetchAndSetCreators(API.topWeeklyCreators([45, 54, 30, 31, 64, 29], 'currentEngagements', CREATOR_LIST_SIZE), setArtists as any);
    };

    if (!hasFetched.current && API) {
      hasFetched.current = true;
      getCreators();
    }
  }, []);

  // All roles use the same date range, so compute dates once from any response
  const startDate = photographers?.startDate
    || videographers?.startDate || artists?.startDate || creators?.startDate;
  const endDate = photographers?.endDate
    || videographers?.endDate || artists?.endDate || creators?.endDate;
  const dateRange = datesToPostedAts(startDate || '', endDate || '');

  return (
    <div id="top-creators" className="bg-alpineTeal text-white">
      <div className="max-w-screen-xl m-auto p-6">
        <h2 className="text-2xl font-bold mb-4">Creator Highlights</h2>
        {!!photographers?.creators?.length && dateRange && startDate && endDate && (
          <CreatorList
            title={`Photographers with the most engagements ${startDate} - ${endDate}`}
            creators={photographers.creators}
            moreUrl={`/creators?role=Photographer~24&sort=currentEngagements&minPostedAt=${dateRange.minPostedAt}&maxPostedAt=${dateRange.maxPostedAt}`}
            moreTitle="View top photographers"
            id="photographers"
          />
        )}
        {!!videographers?.creators?.length && dateRange && startDate && endDate && (
          <CreatorList
            title={`Videographers with the most engagements ${startDate} - ${endDate}`}
            creators={videographers.creators}
            moreUrl={`/creators?role=Videographer~25&sort=currentEngagements&minPostedAt=${dateRange.minPostedAt}&maxPostedAt=${dateRange.maxPostedAt}`}
            moreTitle="View top videographers"
            id="videographers"
          />
        )}
        {!!artists?.creators?.length && dateRange && startDate && endDate && (
          <CreatorList
            title={`Artists, designers, and animators with the most engagements ${startDate} - ${endDate}`}
            creators={artists.creators}
            moreUrl={`/creators?role=Graphic+Artist~45&role=Graphic+Designer~54&role=Illustrator~30&role=Animator~31&role=Motion+Designer~64&role=VFX+Artist~29&sort=currentEngagements&minPostedAt=${dateRange.minPostedAt}&maxPostedAt=${dateRange.maxPostedAt}`}
            moreTitle="View top artists, designers, and animators"
            id="artists"
          />
        )}
        {!!creators?.creators?.length && dateRange && startDate && endDate && (
          <CreatorList
            title={`Creators with the most engagements ${startDate} - ${endDate}`}
            creators={creators.creators}
            moreUrl={`/creators?sort=currentEngagements&minPostedAt=${dateRange.minPostedAt}&maxPostedAt=${dateRange.maxPostedAt}`}
            moreTitle="View top creators"
            id="creators"
          />
        )}
      </div>
    </div>

  );
};
